import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { User, Upload, X, FileBox, Loader2, Image as ImageIcon, ShieldCheck, Bell } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { saveDesignerProfile } from "../services/profiles";
import { listActiveCasesForDesigner } from "../services/cases";
import { uploadFile } from "../services/storage";
import { getCaseCadFiles, isCadFile } from "../lib/cad";
import PayoutPolicyDialog from "../components/PayoutPolicyDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

export default function DesignerProfileEdit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();

  const [form, setForm] = useState({
    experience: "", specialties: "", software: "", turnaround: "", portfolio: "",
    description: "", languages: "", photo: "",
  });
  const [portfolioItems, setPortfolioItems] = useState([]); // manual + featured
  const [featuredCaseIds, setFeaturedCaseIds] = useState([]);
  const [newCaseEmails, setNewCaseEmails] = useState(true);
  const [completed, setCompleted] = useState([]);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [portBusy, setPortBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);

  useEffect(() => {
    const p = profile?.designerProfile;
    if (p) {
      setForm((f) => ({ ...f, ...p }));
      const items = Array.isArray(p.portfolioItems) ? p.portfolioItems : [];
      setPortfolioItems(items);
      setFeaturedCaseIds(items.filter((it) => it.caseId).map((it) => it.caseId));
    }
    // Default ON: only false when the user has explicitly opted out.
    if (profile) setNewCaseEmails(profile.newCaseEmails !== false);
  }, [profile]);

  useEffect(() => {
    if (!user) return;
    listActiveCasesForDesigner(user.uid)
      .then((list) => setCompleted(list.filter((c) => c.status === "Completed")))
      .catch(() => {});
  }, [user]);

  const set = (k, v) => setForm((ff) => ({ ...ff, [k]: v }));

  const uploadPhoto = async (file) => {
    if (!file) return;
    setPhotoBusy(true);
    try {
      const u = await uploadFile(file, `portfolio/${user.uid}`);
      set("photo", u.url);
    } catch (e) {
      toast.error(e?.message || "Upload failed");
    } finally {
      setPhotoBusy(false);
    }
  };

  const uploadPortfolio = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setPortBusy(true);
    try {
      for (const file of files) {
        const u = await uploadFile(file, `portfolio/${user.uid}`);
        setPortfolioItems((prev) => [...prev, { url: u.url, name: u.name, type: isCadFile(file) ? "cad" : "image" }]);
      }
    } catch (e) {
      toast.error(e?.message || "Upload failed");
    } finally {
      setPortBusy(false);
    }
  };

  const removeItem = (idx) => setPortfolioItems((prev) => prev.filter((_, i) => i !== idx));
  const toggleFeatured = (id) =>
    setFeaturedCaseIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const manual = portfolioItems.filter((it) => !it.caseId);
      const featured = completed
        .filter((c) => featuredCaseIds.includes(c.id))
        .map((c) => {
          const cad = getCaseCadFiles(c)[0];
          return {
            caseId: c.id,
            name: c.title,
            url: cad?.url || c.photos?.[0] || "",
            type: cad ? "cad" : "image",
          };
        })
        .filter((it) => it.url);
      await saveDesignerProfile(user.uid, { ...form, portfolioItems: [...manual, ...featured] }, { newCaseEmails });
      await refreshProfile();
      toast.success(t("profile.saved"));
      navigate("/designer");
    } catch (err) {
      toast.error(err?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { key: "experience", label: t("profile.experience"), ph: t("profile.experiencePlaceholder"), type: "input" },
    { key: "specialties", label: t("profile.specialties"), ph: t("profile.specialtiesPlaceholder"), type: "input" },
    { key: "software", label: t("profile.software"), ph: t("profile.softwarePlaceholder"), type: "input" },
    { key: "languages", label: t("profile.languages"), ph: t("profile.languagesPlaceholder"), type: "input" },
    { key: "turnaround", label: t("profile.turnaround"), ph: t("profile.turnaroundPlaceholder"), type: "input" },
  ];

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="mb-8 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <User className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">{t("profile.title")}</h1>
          <p className="text-sm text-muted-foreground">{profile?.name}</p>
        </div>
      </div>

      <form onSubmit={save} className="space-y-5 rounded-lg border border-border bg-card p-6">
        {/* Photo */}
        <div className="space-y-1.5">
          <Label>{t("profile.photo")}</Label>
          <div className="flex items-center gap-4">
            {form.photo ? (
              <img src={form.photo} alt="" className="h-16 w-16 rounded-full border border-border object-cover" data-testid="profile-photo-preview" />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary text-muted-foreground"><User className="h-7 w-7" /></div>
            )}
            <label className="flex cursor-pointer items-center gap-2 rounded-full border border-border px-4 py-2 text-sm hover:bg-secondary/50">
              {photoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {t("profile.uploadPhoto")}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadPhoto(e.target.files[0]); e.target.value = ""; }} data-testid="profile-photo-input" />
            </label>
          </div>
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <Label>{t("profile.description")}</Label>
          <Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder={t("profile.descriptionPlaceholder")} data-testid="profile-description" />
        </div>

        {fields.map((f) => (
          <div key={f.key} className="space-y-1.5">
            <Label>{f.label}</Label>
            <Input value={form[f.key]} onChange={(e) => set(f.key, e.target.value)} placeholder={f.ph} data-testid={`profile-${f.key}`} />
          </div>
        ))}

        <div className="space-y-1.5">
          <Label>{t("profile.portfolio")}</Label>
          <Textarea rows={3} value={form.portfolio} onChange={(e) => set("portfolio", e.target.value)} placeholder={t("profile.portfolioPlaceholder")} data-testid="profile-portfolio" />
        </div>

        {/* Visual portfolio */}
        <div className="space-y-2 rounded-lg border border-border p-4">
          <Label className="flex items-center gap-1.5"><ImageIcon className="h-4 w-4" /> {t("profile.portfolioSection")}</Label>
          <p className="text-xs text-muted-foreground">{t("profile.portfolioHint")}</p>
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-secondary/30 p-3 text-sm text-muted-foreground hover:border-slate-300">
            {portBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {t("profile.portfolioUpload")}
            <input type="file" accept="image/*,.stl,.ply,.obj" multiple className="hidden" onChange={(e) => { uploadPortfolio(e.target.files); e.target.value = ""; }} data-testid="portfolio-upload-input" />
          </label>
          {portfolioItems.filter((it) => !it.caseId).length > 0 && (
            <div className="grid grid-cols-3 gap-2" data-testid="portfolio-manual-items">
              {portfolioItems.map((it, i) => it.caseId ? null : (
                <div key={i} className="relative">
                  {it.type === "image" ? (
                    <img src={it.url} alt={it.name} className="h-20 w-full rounded-md border border-border object-cover" />
                  ) : (
                    <div className="flex h-20 flex-col items-center justify-center rounded-md border border-border bg-secondary/40 text-center text-[10px] text-muted-foreground"><FileBox className="h-4 w-4" /><span className="truncate px-1">{it.name}</span></div>
                  )}
                  <button type="button" onClick={() => removeItem(i)} className="absolute -right-1.5 -top-1.5 rounded-full bg-background p-0.5 shadow" data-testid={`portfolio-remove-${i}`}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {completed.length > 0 && (
            <div className="mt-2">
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">{t("profile.featureCompleted")}</p>
              <div className="space-y-1">
                {completed.map((c) => (
                  <label key={c.id} className="flex cursor-pointer items-center gap-2 text-sm" data-testid={`feature-case-${c.id}`}>
                    <input type="checkbox" checked={featuredCaseIds.includes(c.id)} onChange={() => toggleFeatured(c.id)} /> {c.title}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4" data-testid="new-case-emails-section">
          <div className="flex items-start gap-2.5">
            <Bell className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            <div>
              <p className="text-sm font-medium">{t("profile.newCaseEmails")}</p>
              <p className="text-xs text-muted-foreground">{t("profile.newCaseEmailsHint")}</p>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={newCaseEmails}
            onClick={() => setNewCaseEmails((v) => !v)}
            data-testid="new-case-emails-toggle"
            className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${newCaseEmails ? "bg-brand" : "bg-slate-300"}`}
          >
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${newCaseEmails ? "left-[22px]" : "left-0.5"}`} />
          </button>
        </div>

        <Button type="submit" disabled={saving} className="w-full rounded-full bg-brand text-white hover:bg-brand/90" data-testid="save-profile-button">
          {t("common.save")}
        </Button>

        <button type="button" onClick={() => setPolicyOpen(true)} className="flex w-full items-center justify-center gap-1.5 text-xs font-medium text-brand hover:underline" data-testid="view-payout-policy-link">
          <ShieldCheck className="h-3.5 w-3.5" /> {t("payoutPolicy.view")}
        </button>
      </form>

      <PayoutPolicyDialog open={policyOpen} onOpenChange={setPolicyOpen} mode="view" />
    </div>
  );
}
